import { type HTMLAttributes, type PropsWithChildren, useMemo } from 'react'
import type { SetRequired, TupleToUnion } from 'type-fest'

import { cn } from '@mntn-dev/ui-utilities'

import { getTestProps, type TestIds } from '../../utils/testing.ts'
import { Icon, type IconProps } from '../icon/icon.tsx'

const tagTypes = [
  'brand',
  'default',
  'info',
  'success',
  'warning',
  'error',
  'notice',
] as const
type TagType = TupleToUnion<typeof tagTypes>

type TagBaseProps = Readonly<
  HTMLAttributes<HTMLSpanElement> &
    PropsWithChildren<
      TestIds & {
        type?: TagType
        icon?: SetRequired<Partial<IconProps>, 'name'>
        dismissible?: boolean
        onDismiss?: () => void
        textTransform?: 'uppercase' | 'none'
      }
    >
>

const tagTypeClassNameMap: Record<TagType, string> = {
  brand: 'tag-type-brand',
  default: 'tag-type-default',
  info: 'tag-type-info',
  success: 'tag-type-success',
  warning: 'tag-type-warning',
  error: 'tag-type-error',
  notice: 'tag-type-notice',
}

export const getTagTypeClassName = (type: TagType) => {
  return tagTypeClassNameMap[type]
}

const TagBase = ({
  children,
  className,
  type = 'default',
  icon,
  dismissible,
  dataTestId,
  dataTrackingId,
  onDismiss,
  textTransform,
  ...props
}: TagBaseProps) => {
  const tagTypeClassName = useMemo(() => getTagTypeClassName(type), [type])

  const {
    name: iconName,
    size: iconSize = 'xs',
    color: iconColor = 'current',
    fill: iconFill = 'solid',
    ...iconProps
  } = icon || {}

  return (
    <span
      {...props}
      className={cn(
        'qf-tag',
        'h-5 text-2xs px-2 gap-x-1',
        'inline-flex items-center justify-center font-medium rounded-full border overflow-hidden min-w-fit max-w-full backdrop-blur-md',
        tagTypeClassName,
        {
          uppercase: textTransform === 'uppercase',
        },
        className
      )}
      {...getTestProps({ dataTestId, dataTrackingId })}
    >
      {iconName && (
        <Icon
          name={iconName}
          size={iconSize}
          fill={iconFill}
          color={iconColor}
          {...iconProps}
        />
      )}
      <span className="whitespace-nowrap text-ellipsis max-w-full overflow-hidden">
        {children}
      </span>

      {dismissible && (
        <button
          type="button"
          className="group relative -mr-1 h-3.5 w-3.5 rounded-xs"
          onClick={onDismiss}
        >
          <span className="sr-only">Remove</span>
          <Icon fill="solid" name="close" size="xs" color="current" />
          <span className="absolute -inset-1" />
        </button>
      )}
    </span>
  )
}

export { TagBase, type TagBaseProps, type TagType, tagTypes }
